import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useContacts } from "@/hooks/useContacts";
import { useOpportunities } from "@/hooks/useOpportunities";
import { usePipelinesSettings } from "@/hooks/usePipelinesSettings";
import { useTags } from "@/hooks/useTags";
import { getContactPhone } from "@/utils/contactUtils";
import { X, Search } from "lucide-react";

interface AddOpportunityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPipelineId: string;
  pipelines?: Array<{id: string; name: string; is_active?: boolean}>;
  stages?: Array<{id: string; name: string; pipeline_id: string; is_active?: boolean}>;
  onPipelineChange?: (pipelineId: string) => void;
  createOpportunity?: any;
  checkExistingOpportunity?: any;
}

const AddOpportunityDialog: React.FC<AddOpportunityDialogProps> = ({
  open,
  onOpenChange,
  selectedPipelineId,
  pipelines: externalPipelines,
  stages: externalStages,
  onPipelineChange,
  createOpportunity: externalCreateOpportunity,
  checkExistingOpportunity: externalCheckExistingOpportunity
}) => {
  const { contacts, createContact } = useContacts();
  const { createOpportunity: internalCreateOpportunity, checkExistingOpportunity: internalCheckExistingOpportunity } = useOpportunities(selectedPipelineId);
  const { stages: internalStages, pipelines: internalPipelines } = usePipelinesSettings();
  const { tags: accountTags } = useTags();

  const createOpportunity = externalCreateOpportunity || internalCreateOpportunity;
  const checkExistingOpportunity = externalCheckExistingOpportunity || internalCheckExistingOpportunity;

  const stages = externalStages || internalStages;
  const pipelines = externalPipelines || internalPipelines;

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    contactId: '',
    newContactName: '',
    newContactPhone: '',
    title: '',
    product: '',
    price: '',
    pipelineId: selectedPipelineId,
    stageId: '',
  });

  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [isNewContact, setIsNewContact] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const tagContainerRef = useRef<HTMLDivElement>(null);

  const pipelineStages = stages.filter(s => s.pipeline_id === (formData.pipelineId || selectedPipelineId) && s.is_active);
  const firstStageId = pipelineStages.length > 0 ? pipelineStages[0].id : '';

  // Update stageId when pipeline changes
  useEffect(() => {
    if (open && formData.pipelineId && pipelineStages.length > 0 && !formData.stageId) {
      setFormData(prev => ({ ...prev, stageId: firstStageId }));
    }
  }, [open, formData.pipelineId, firstStageId, pipelineStages.length]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        contactId: '',
        newContactName: '',
        newContactPhone: '',
        title: '',
        product: '',
        price: '',
        pipelineId: selectedPipelineId,
        stageId: firstStageId,
      });
      setIsNewContact(false);
      setSelectedContact(null);
      setSearchContact('');
      setTags([]);
      setTagInput('');
      setMessage(null);
    }
  }, [open, selectedPipelineId, firstStageId]);

  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchContact.toLowerCase();
    return (
      contact.display_name?.toLowerCase().includes(searchLower) ||
      contact.phone?.toLowerCase().includes(searchLower) ||
      contact.push_name_last?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    if (!open) {
      setFormData({
        contactId: '',
        newContactName: '',
        newContactPhone: '',
        title: '',
        product: '',
        price: '',
        pipelineId: selectedPipelineId,
        stageId: '',
      });
      setSelectedContact(null);
      setIsNewContact(false);
      setSearchContact('');
      setShowContactDropdown(false);
      setTags([]);
      setTagInput('');
      setMessage(null);
    }
  }, [open, selectedPipelineId]);

  const handleContactSelect = (contact: any) => {
    const phoneNumber = getContactPhone(contact);

    console.log('📞 [AddOpportunity] Contato selecionado:', {
      id: contact.id,
      display_name: contact.display_name,
      phone: contact.phone,
      jid: contact.jid,
      phone_extracted: phoneNumber,
      push_name_last: contact.push_name_last
    });

    setSelectedContact(contact);
    setFormData(prev => ({
      ...prev,
      contactId: contact.id,
      newContactPhone: phoneNumber
    }));
    setSearchContact(contact.display_name || phoneNumber || '');
    setShowContactDropdown(false);
    setIsNewContact(false);
  };

  const handleNewContact = () => {
    setIsNewContact(true);
    setSelectedContact(null);
    setFormData(prev => ({ ...prev, contactId: '', newContactPhone: '', newContactName: '' }));
    setShowContactDropdown(false);
  };

  const formatCurrency = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    const amount = parseFloat(numbers) / 100;
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numbers = value.replace(/\D/g, '');

    if (numbers === '') {
      setFormData(prev => ({ ...prev, price: '' }));
      return;
    }

    const formatted = formatCurrency(numbers);
    setFormData(prev => ({ ...prev, price: formatted }));
  };

  const getPriceValue = (): number => {
    if (!formData.price) return 0;
    const numbers = formData.price.replace(/\D/g, '');
    return parseFloat(numbers) / 100;
  };

  const filteredTagSuggestions = accountTags
    .filter(t => t.is_active)
    .filter(t => !tags.includes(t.name))
    .filter(t => !tagInput || t.name.toLowerCase().includes(tagInput.toLowerCase()));

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedTag = tagInput.trim();

      if (trimmedTag && !tags.includes(trimmedTag)) {
        setTags([...tags, trimmedTag]);
        setTagInput('');
        setShowTagDropdown(false);
      }
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      setTags(tags.slice(0, -1));
    } else if (e.key === 'Escape') {
      setShowTagDropdown(false);
    }
  };

  const handleSelectTag = (tagName: string) => {
    if (!tags.includes(tagName)) {
      setTags([...tags, tagName]);
    }
    setTagInput('');
    setShowTagDropdown(false);
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const pipelineToUse = formData.pipelineId || selectedPipelineId;
    const stageToUse = formData.stageId || firstStageId;

    if (!pipelineToUse || !stageToUse) {
      setMessage({ type: 'error', text: 'Pipeline ou estágio não encontrado' });
      return;
    }

    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Nome da oportunidade é obrigatório' });
      return;
    }

    if (isNewContact && (!formData.newContactName.trim() || !formData.newContactPhone.trim())) {
      setMessage({ type: 'error', text: 'Nome e telefone do contato são obrigatórios' });
      return;
    }

    if (!isNewContact && !formData.contactId) {
      setMessage({ type: 'error', text: 'Selecione um contato' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      let contactIdToUse = formData.contactId;
      let contactNameToUse = selectedContact?.display_name || selectedContact?.push_name_last || '';

      if (isNewContact) {
        const phoneFormatted = formData.newContactPhone.replace(/\D/g, '');
        const jid = `${phoneFormatted}@s.whatsapp.net`;

        const created = await createContact({
          jid,
          is_group: false,
          display_name: formData.newContactName,
          phone: formData.newContactPhone,
          push_name_last: formData.newContactName
        } as any);

        if (!created.success) {
          setMessage({
            type: 'error',
            text: created.error || 'Erro ao criar contato',
          });
          return;
        }

        const newContact = contacts.find(c => c.phone === formData.newContactPhone);
        contactIdToUse = newContact?.id || '';
        contactNameToUse = formData.newContactName;
      }

      // Check if opportunity already exists for this contact in this pipeline
      if (contactIdToUse) {
        const existing = await checkExistingOpportunity(contactIdToUse, pipelineToUse);
        if (existing) {
          setMessage({
            type: 'error',
            text: `Já existe uma oportunidade para este contato neste pipeline: "${existing.title}"`
          });
          setLoading(false);
          return;
        }
      }

      const opportunity = await createOpportunity({
        pipeline_id: pipelineToUse,
        stage_id: stageToUse,
        contact_id: contactIdToUse || undefined,
        title: `${formData.title} (${contactNameToUse})`,
        product: formData.product || undefined,
        price: getPriceValue(),
        tags: tags
      });

      if (opportunity) {
        // Close dialog immediately
        onOpenChange(false);

        // Reset form
        setFormData({
          contactId: '',
          newContactName: '',
          newContactPhone: '',
          title: '',
          product: '',
          price: '',
          pipelineId: selectedPipelineId,
          stageId: ''
        });
        setTags([]);
        setSearchContact('');

        // Switch pipeline if needed (only when creating in different pipeline)
        if (onPipelineChange && pipelineToUse !== selectedPipelineId) {
          setTimeout(() => {
            onPipelineChange(pipelineToUse);
          }, 100);
        }
      } else {
        setMessage({ type: 'error', text: 'Erro ao criar oportunidade' });
      }
    } catch (error) {
      console.error('Erro ao criar oportunidade:', error);
      setMessage({ type: 'error', text: 'Erro ao criar oportunidade' });
    } finally {
      setLoading(false);
    }
  };

  // Debug: Log estado atual
  console.log('🔍 [AddOpportunity] Estado atual:', {
    isNewContact,
    selectedContact: selectedContact ? {
      id: selectedContact.id,
      phone: selectedContact.phone,
      display_name: selectedContact.display_name
    } : null,
    formDataPhone: formData.newContactPhone
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex !max-w-[600px] rounded-2xl w-full p-6 border-border bg-card flex-col max-h-[90vh] overflow-y-auto dark:border-[#3F3F464D] dark:bg-[#18181B]">
        <DialogHeader>
          <DialogTitle className="text-[20px] text-foreground dark:text-neutral-50 font-semibold">
            Nova Oportunidade
          </DialogTitle>
        </DialogHeader>

        {message && (
          <div className={`p-3 rounded-md mb-2 ${
            message.type === 'success'
              ? 'bg-green-500/20 border border-green-500/30 text-green-300'
              : 'bg-red-500/20 border border-red-500/30 text-red-300'
          }`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
          <div className="flex flex-col gap-2 relative">
            <Label htmlFor="contact" className="text-foreground dark:text-neutral-50">Contato *</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
              <Input
                id="contact"
                value={searchContact}
                onChange={(e) => {
                  setSearchContact(e.target.value);
                  setShowContactDropdown(true);
                }}
                onFocus={() => setShowContactDropdown(true)}
                placeholder="Pesquisar contato pelo nome..."
                className="bg-white border-border text-foreground pl-10 dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                autoComplete="off"
              />
            </div>

            {showContactDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-xl max-h-[200px] overflow-y-auto z-50 shadow-md dark:bg-[#27272A] dark:border-[#3F3F464D] dark:rounded-md dark:shadow-lg">
                <button
                  type="button"
                  onClick={handleNewContact}
                  className="w-full text-left px-4 py-2 hover:bg-[#F1F5F9] text-foreground border-b border-border flex items-center gap-2 dark:hover:bg-[#3F3F46] dark:text-neutral-50 dark:border-[#3F3F464D]"
                >
                  <span className="text-green-400">+</span> Novo Contato
                </button>
                {filteredContacts.length === 0 ? (
                  <div className="px-4 py-3 text-neutral-400 text-sm">
                    Nenhum contato encontrado
                  </div>
                ) : (
                  filteredContacts.map(contact => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => handleContactSelect(contact)}
                      className="w-full text-left px-4 py-2 hover:bg-[#F1F5F9] text-foreground border-b border-border last:border-b-0 dark:hover:bg-[#3F3F46] dark:text-neutral-50 dark:border-[#3F3F464D]"
                    >
                      <div className="font-medium">{contact.display_name || contact.phone}</div>
                      {contact.display_name && contact.phone && (
                        <div className="text-xs text-neutral-400">{contact.phone}</div>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {isNewContact && (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="newContactName" className="text-foreground dark:text-neutral-50">Nome do Contato *</Label>
                <Input
                  id="newContactName"
                  value={formData.newContactName}
                  onChange={(e) => setFormData(prev => ({ ...prev, newContactName: e.target.value }))}
                  placeholder="Ex: João Silva"
                  className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="newContactPhone" className="text-foreground dark:text-neutral-50">Telefone *</Label>
                <Input
                  id="newContactPhone"
                  value={formData.newContactPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, newContactPhone: e.target.value }))}
                  placeholder="Ex: 5511999999999"
                  className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                  required
                />
              </div>
            </>
          )}

          {!isNewContact && formData.contactId && (
            <div className="flex flex-col gap-2">
              <Label className="text-foreground dark:text-neutral-50">Telefone</Label>
              <Input
                value={formData.newContactPhone || 'Não informado'}
                disabled
                className="bg-[#F8FAFC] border-border text-muted-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-400"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="pipeline" className="text-foreground dark:text-neutral-50">Pipeline *</Label>
            <Select
              value={formData.pipelineId}
              onValueChange={(value) => setFormData(prev => ({ ...prev, pipelineId: value, stageId: '' }))}
            >
              <SelectTrigger className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50">
                <SelectValue placeholder="Selecione uma pipeline" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                {pipelines.filter(p => p.is_active !== false).map((pipeline) => (
                  <SelectItem key={pipeline.id} value={pipeline.id} className="text-foreground dark:text-neutral-50">
                    {pipeline.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="stage" className="text-foreground dark:text-neutral-50">Etapa *</Label>
            <Select
              value={formData.stageId}
              onValueChange={(value) => setFormData(prev => ({ ...prev, stageId: value }))}
              disabled={!formData.pipelineId}
            >
              <SelectTrigger className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50">
                <SelectValue placeholder="Selecione uma etapa" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                {pipelineStages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id} className="text-foreground dark:text-neutral-50">
                    {stage.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="title" className="text-foreground dark:text-neutral-50">Nome da Oportunidade *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex: Venda de produto X"
              className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="product" className="text-foreground dark:text-neutral-50">Produto</Label>
            <Input
              id="product"
              value={formData.product}
              onChange={(e) => setFormData(prev => ({ ...prev, product: e.target.value }))}
              placeholder="Ex: Consultoria Premium"
              className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="price" className="text-foreground dark:text-neutral-50">Valor</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">R$</span>
              <Input
                id="price"
                type="text"
                value={formData.price}
                onChange={handlePriceChange}
                placeholder="0,00"
                className="bg-white border-border text-foreground pl-12 dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 relative" ref={tagContainerRef}>
            <Label htmlFor="tags" className="text-foreground dark:text-neutral-50">Tags</Label>
            <div className="flex flex-wrap gap-2 p-2 bg-white border border-border rounded-xl min-h-[42px] dark:bg-[#27272A] dark:border-[#3F3F464D] dark:rounded-md">
              {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm bg-[#DBEAFE] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:text-red-400 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </span>
              ))}
              <Input
                id="tags"
                value={tagInput}
                onChange={(e) => {
                  setTagInput(e.target.value);
                  setShowTagDropdown(true);
                }}
                onFocus={() => setShowTagDropdown(true)}
                onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                onKeyDown={handleTagKeyDown}
                placeholder={tags.length === 0 ? "Buscar ou criar tag..." : ""}
                className="flex-1 min-w-[120px] border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground dark:text-neutral-50 p-0"
              />
            </div>
            {showTagDropdown && filteredTagSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-xl max-h-[200px] overflow-y-auto z-50 shadow-md dark:bg-[#27272A] dark:border-[#3F3F464D] dark:rounded-md dark:shadow-lg">
                {filteredTagSuggestions.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectTag(tag.name)}
                    className="w-full text-left px-4 py-2 hover:bg-[#3F3F46] text-neutral-50 border-b border-[#3F3F464D] last:border-b-0 flex items-center gap-2"
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-neutral-400">Digite para buscar tags ou pressione Enter para criar nova</p>
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-border bg-transparent hover:bg-[#F1F5F9] text-foreground dark:border-[#3F3F464D] dark:hover:bg-[#27272A] dark:text-neutral-50"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              disabled={loading}
            >
              {loading ? 'Criando...' : 'Criar Oportunidade'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddOpportunityDialog;
